/**
 * Natywny klient WebAuthn — manualna konwersja base64url ↔ ArrayBuffer i wywołanie navigator.credentials.
 * Zastępuje @github/webauthn-json (zdeprecjonowana; rzucała w Safari generyczny "TypeError: Type error",
 * bo jej konwersja produkowała strukturę odrzucaną przez WebKit). Tu mamy pełną kontrolę nad kształtem,
 * a wynik (base64url, bez paddingu) jest dokładnie tym, czego oczekuje webauthn-ruby (from_create/from_get).
 */

const b64urlToBuffer = (b64url: string): ArrayBuffer => {
  const pad = (4 - (b64url.length % 4)) % 4;
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(pad);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const bufferToB64url = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// Rejestracja: opcje z webauthn-ruby (options_for_create.as_json) → navigator.credentials.create → JSON dla serwera.
export const registerPasskey = async (options: any) => {
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: b64urlToBuffer(options.challenge),
    user: { ...options.user, id: b64urlToBuffer(options.user.id) },
    excludeCredentials: (options.excludeCredentials ?? []).map((c: any) => ({
      ...c,
      id: b64urlToBuffer(c.id)
    }))
  };

  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential;
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    type: cred.type,
    rawId: bufferToB64url(cred.rawId),
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON),
      attestationObject: bufferToB64url(r.attestationObject)
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {}
  };
};

// Logowanie: opcje z webauthn-ruby (options_for_get.as_json) → navigator.credentials.get → JSON dla serwera.
export const authenticatePasskey = async (options: any) => {
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: b64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials ?? []).map((c: any) => ({
      ...c,
      id: b64urlToBuffer(c.id)
    }))
  };

  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential;
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    type: cred.type,
    rawId: bufferToB64url(cred.rawId),
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON),
      authenticatorData: bufferToB64url(r.authenticatorData),
      signature: bufferToB64url(r.signature),
      userHandle: r.userHandle ? bufferToB64url(r.userHandle) : null
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {}
  };
};
